import { keyframes, styled } from "goober";
import type React from "react";
import { useEffectEvent, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  popModal,
  pushModal,
  stackTransform,
  useModalPosition,
} from "./modalStack";

const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Dialog = styled("dialog")`
  box-shadow: var(--elevation-high);
  border: 0;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  border-radius: 16px;
  padding: 0;
  transform-origin: center bottom;
  transform: none;
  transition: transform var(--motion-fast) var(--ease-out);

  &[data-stacked="true"] {
    transform: translateY(calc(var(--modal-offset) * 1px))
      scale(var(--modal-scale));
  }

  /* Stacked modals peek out at the bottom; hide their content so the peek reads
   as a blank stacked card instead of leaking the previous modal's footer into
   the top modal's rounded corners. An alert (e.g. a confirmation) does not
   stack the modal beneath it, so that modal stays visible and is dimmed by the
   alert's own backdrop instead. */
  & > * {
    transition: opacity var(--motion-fast) var(--ease-out);
  }
  &[data-stacked="true"] > * {
    opacity: 0;
  }

  @starting-style {
    transform: translateY(6px);
  }

  &::backdrop {
    background: var(--modal-backdrop);
    opacity: 0;
  }
  &[data-bottom="true"]::backdrop,
  &[data-alert="true"]::backdrop {
    opacity: 1;
    animation: ${backdropFadeIn} var(--motion-fast) var(--ease-out);
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  alert?: boolean;
  closeOnBackdropClick?: boolean;
  initialFocusRef?: { readonly current: HTMLElement | null };
  className?: string;
  id?: string;
  "aria-label"?: string;
};

export function Modal({
  open,
  onClose,
  children,
  style,
  alert = false,
  closeOnBackdropClick = true,
  initialFocusRef,
  className,
  id,
  "aria-label": ariaLabel,
}: Props) {
  const [el, setEl] = useState<HTMLDialogElement | null>(null);
  const [token] = useState(() => Symbol());

  const { depth, isTop, isBottom, coveredByAlert } = useModalPosition(token);
  const { offset, scale } = stackTransform(depth);
  const stacked = !isTop && !coveredByAlert;

  const handleDialogClose = useEffectEvent(() => onClose());
  const handleEscape = useEffectEvent((e: KeyboardEvent) => {
    if (e.key !== "Escape" || !isTop) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    onClose();
  });

  useLayoutEffect(() => {
    if (!open) return;
    pushModal(token, alert);
    return () => popModal(token);
  }, [open, token, alert]);

  useLayoutEffect(() => {
    if (!el) return;
    const previousFocus = document.activeElement;
    el.showModal();
    const focusTarget = initialFocusRef?.current ?? el;
    focusTarget.focus();
    if (
      focusTarget instanceof HTMLInputElement ||
      focusTarget instanceof HTMLTextAreaElement
    )
      focusTarget.select();
    const handleClose = () => handleDialogClose();
    const handleCancel = (e: Event) => e.preventDefault();
    const handleKeyDown = (e: KeyboardEvent) => handleEscape(e);
    el.addEventListener("close", handleClose);
    el.addEventListener("cancel", handleCancel);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      el.removeEventListener("close", handleClose);
      el.removeEventListener("cancel", handleCancel);
      document.removeEventListener("keydown", handleKeyDown, true);
      // Restoring focus before removal can leave the previous input unable to receive typing.
      requestAnimationFrame(() => {
        if (el.isConnected) return;
        el.close();
        const activeDialog = document.activeElement?.closest(
          'dialog[open], [role="dialog"]'
        );
        if (activeDialog && !activeDialog.contains(previousFocus)) return;
        if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
          previousFocus.focus({ preventScroll: true });
        }
      });
    };
  }, [el, initialFocusRef]);

  if (!open) return null;

  const onClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    e.stopPropagation();
    if (closeOnBackdropClick && e.target === el && isTop) onClose();
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return createPortal(
    <Dialog
      ref={setEl}
      className={className}
      id={id}
      aria-label={ariaLabel}
      tabIndex={-1}
      data-stacked={stacked}
      data-alert={alert}
      data-bottom={isBottom}
      onClick={onClick}
      onMouseDown={stop}
      onMouseUp={stop}
      style={
        {
          ...style,
          "--modal-offset": offset,
          "--modal-scale": scale,
        } as React.CSSProperties
      }
    >
      {children}
    </Dialog>,
    document.body
  );
}
